<template>
  <div id="app" :class="moonTheme ? 'theme-moon' : 'theme-garden'">
    <router-view/>
  </div>
</template>

<script>
import { moonSkinActive } from '@/utils/moonSkin';

export default {
  name: 'App',
  computed: {
    // 主题切换：中秋活动期（2026-09-18 ~ 2026-10-12）用月满主题，其余日常花园主题，
    // 活动结束自动切回（复用 moonSkin 的窗口判断）
    moonTheme() {
      return moonSkinActive();
    }
  },
  watch: {
    // PC 端内容区居中后，body 两侧留白的装饰背景跟随主题切换
    moonTheme: {
      handler(v) {
        document.body.classList.toggle('body-moon', !!v);
      },
      immediate: true
    }
  }
}
</script>

<style>
/* =====================================================================
   全站主题系统 —— 两套主题共用一套 CSS 变量，所有组件经 var() 取色。

   日常「清新花园」= .theme-garden（浅色）；中秋「月满广寒」= .theme-moon（深色）。
   class 由 App.vue 按活动窗口自动切换，活动结束自动回日常主题。
   ===================================================================== */

/* ---------- 变量定义：日常 · 清新花园（浅色） ---------- */
#app.theme-garden {
  --bg-page: #f5f9f3;
  --bg-card: #ffffff;
  --bg-nav: #ffffff;
  --border-color: rgba(123, 174, 127, 0.2);
  --text-primary: #2d3436;
  --text-secondary: #95a5a6;
  --btn-primary-bg: linear-gradient(135deg, #7bae7f, #a8e6cf);
  --btn-primary-text: #ffffff;
  --tag-bg: #e8f5e9;
  --tag-text: #7bae7f;
  --tag-border: none;
  --shadow: 0 2px 8px rgba(123, 174, 127, 0.08);
}

/* ---------- 变量定义：中秋 · 月满广寒（深色） ---------- */
#app.theme-moon {
  --bg-page: linear-gradient(180deg, #0d1b2a, #1b263b);
  --bg-card: rgba(255, 255, 255, 0.08);
  --bg-nav: rgba(13, 27, 42, 0.9);
  --border-color: rgba(244, 208, 63, 0.3);
  --text-primary: #f4d03f;
  --text-secondary: #a8b2d1;
  --btn-primary-bg: linear-gradient(135deg, #e67e22, #f39c12);
  --btn-primary-text: #ffffff;
  --tag-bg: transparent;
  --tag-text: #f4d03f;
  --tag-border: 1px solid rgba(244, 208, 63, 0.55);
  --shadow: 0 2px 12px rgba(244, 208, 63, 0.1);
}

/* ---------- 页面基底 ---------- */
#app {
  min-height: 100vh;
  background: var(--bg-page);
  background-attachment: fixed;
  transition: background 0.6s ease;
}

/* =====================================================================
   响应式布局基建（移动端全宽 / PC 端 600px 居中）

   - #app 最大 600px 居中：PC 大屏上内容像"大手机"竖在屏幕中央
   - body 两侧留白用主题渐变 + 圆点纹理装饰，避免大片空白单调
   - fixed 定位类组件（底部抽屉 / 预置 tabbar）同步限制宽度居中
   ===================================================================== */
body {
  margin: 0;
  /* 全站防御横向滚动条（个别超宽表格/装饰溢出时兜底裁切） */
  overflow-x: hidden;
  /* 日常：清新绿渐变 + 圆点园艺纹理 */
  background:
    radial-gradient(circle at 12% 18%, rgba(123, 174, 127, 0.28) 0 14px, transparent 15px),
    radial-gradient(circle at 78% 42%, rgba(168, 230, 207, 0.35) 0 20px, transparent 21px),
    radial-gradient(circle at 35% 76%, rgba(123, 174, 127, 0.22) 0 16px, transparent 17px),
    radial-gradient(circle at 88% 88%, rgba(168, 230, 207, 0.3) 0 18px, transparent 19px),
    linear-gradient(160deg, #eaf5ea 0%, #f7fbf8 45%, #e0efe2 100%);
  background-attachment: fixed;
}

/* 中秋：body 两侧换成深色星空 + 月晕纹理 */
body.body-moon {
  background:
    radial-gradient(circle at 14% 16%, rgba(244, 208, 63, 0.16) 0 16px, transparent 17px),
    radial-gradient(circle at 82% 38%, rgba(232, 126, 40, 0.14) 0 20px, transparent 21px),
    radial-gradient(circle at 30% 72%, rgba(244, 208, 63, 0.12) 0 18px, transparent 19px),
    radial-gradient(circle at 86% 90%, rgba(232, 126, 40, 0.12) 0 16px, transparent 17px),
    linear-gradient(160deg, #0d1b2a 0%, #16283e 50%, #1b263b 100%);
  background-attachment: fixed;
}

/* PC 端内容列：600px 居中 + 侧边柔和阴影（移动端 ≤600px 时自然全宽，无感） */
#app {
  max-width: 600px;
  margin: 0 auto;
  box-shadow: 0 0 48px rgba(123, 174, 127, 0.25);
}

#app.theme-moon {
  box-shadow: 0 0 48px rgba(244, 208, 63, 0.12);
}

/* 底部抽屉（van-popup position=bottom）跟随内容列居中，不被 fixed 拉到全屏；
   safe-area 处理 iPhone 底部横条，内容不被遮挡 */
#app .van-popup--bottom {
  left: 0;
  right: 0;
  margin: 0 auto;
  max-width: 600px;
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
}

/* 预置底部标签栏（当前项目未使用 tabbar，样式先备好居中规则） */
#app .van-tabbar--fixed {
  left: 0;
  right: 0;
  margin: 0 auto;
  max-width: 600px;
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
}

/* ---------- 触摸目标与间距响应式 ----------
   行内 mini 按钮在移动端加大内边距（22px → 28px 高），配合按钮间
   天然留白接近 44px 触达区；全屏 block 主操作按钮本身 ≥44px */
@media (max-width: 768px) {
  #app .van-button--mini {
    padding: 0 12px;
    min-height: 28px;
  }

  /* 花园页标题栏按钮组（一键照料/收获/播种 + 添置/刷新 共 5 个）：
     单独收紧内边距，窄屏一行能放两个以上，最多两行不溢出 */
  #app .pot-title-btns .van-button--mini {
    padding: 0 8px;
  }
}

/* ---------- 窄屏降列（≤380px：iPhone SE 一档） ----------
   van-grid 的 column-num 是内联 flex-basis，用 !important 覆盖降列；
   花神卡 3 → 2（诗句不竖排）、花神合成选择 4 → 3（花名不挤压） */
@media (max-width: 380px) {
  #app .god-grid .van-grid-item {
    flex-basis: 50% !important;
    max-width: 50% !important;
  }

  #app .recipe-grid .van-grid-item {
    flex-basis: 33.333% !important;
    max-width: 33.333% !important;
  }
}

/* PC 端 hover 反馈（无触屏时不显示按压态，改为悬停态） */
@media (hover: hover) {
  #app .van-grid-item__content--clickable:hover {
    background: rgba(123, 174, 127, 0.14);
  }

  #app.theme-moon .van-grid-item__content--clickable:hover {
    background: rgba(255, 255, 255, 0.08);
  }
}

/* ---------- 卡片（cell-group）：所有列表卡、弹层共用 ---------- */
#app .van-cell-group--inset {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  box-sizing: border-box;
  box-shadow: var(--shadow);
}

#app .van-cell-group {
  background: var(--bg-card);
}

/* 中秋：卡片毛玻璃 */
#app.theme-moon .van-cell-group--inset,
#app.theme-moon .van-cell-group {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* ---------- 单元格与文字 ---------- */
#app .van-cell {
  background: transparent;
  color: var(--text-primary);
}

#app .van-cell::after {
  border-color: var(--border-color);
}

#app .van-cell__title {
  color: var(--text-primary);
}

#app .van-cell__label {
  color: var(--text-secondary) !important;
}

#app .van-cell__value {
  color: var(--text-secondary);
}

#app .van-cell-group__title {
  color: var(--text-secondary);
}

/* ---------- 顶部导航（全站 van-nav-bar，含内容层，白带彻底消除） ---------- */
#app .van-nav-bar,
#app .van-nav-bar .van-nav-bar__content {
  background: var(--bg-nav);
}

#app .van-nav-bar {
  border-bottom: 1px solid var(--border-color);
}

/* Vant hairline 分隔线（部分页面 nav-bar 带 --bottom 类）同步主题色 */
#app .van-nav-bar.van-hairline--bottom::after {
  border-color: var(--border-color);
}

/* 中秋：导航毛玻璃 + 标题两侧 🌕🏮（左右各留 40px） */
#app.theme-moon .van-nav-bar,
#app.theme-moon .van-nav-bar .van-nav-bar__content {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

#app .van-nav-bar__title,
#app .van-nav-bar .van-ellipsis {
  color: var(--text-primary);
}

#app .van-nav-bar__text,
#app .van-nav-bar__arrow,
#app .van-nav-bar .van-icon {
  color: var(--text-primary);
}

#app.theme-moon .van-nav-bar__title::before {
  content: '🌕';
  margin-right: 40px;
  opacity: 0.9;
}

#app.theme-moon .van-nav-bar__title::after {
  content: '🏮';
  margin-left: 40px;
  opacity: 0.9;
}

/* ---------- 宫格（首页入口 + 底部功能导航 5 图标） ----------
   白带根源：Vant grid-item 的每个格子内容层自带 background-color:#fff，
   深色下排成一条白带 —— 内容层透明，让主题卡底（--bg-card）透出来 */
#app .van-grid-item__content {
  background: transparent;
}

/* 格子分隔线与按压反馈也走主题色（Vant 默认浅灰在深色下刺眼） */
#app .van-grid-item__content::after {
  border-color: var(--border-color);
}

#app .van-grid-item__content--clickable:active {
  background: rgba(123, 174, 127, 0.12);
}

#app.theme-moon .van-grid-item__content--clickable:active {
  background: rgba(255, 255, 255, 0.06);
}

#app .van-grid-item__icon {
  color: var(--tag-text);
}

#app .van-grid-item__text {
  color: var(--text-secondary);
}

/* ---------- 底部标签栏（van-tabbar，当前项目未使用，预置覆盖备用） ---------- */
#app .van-tabbar,
#app .van-tabbar .van-tabbar-item {
  background: var(--bg-nav);
}

#app.theme-moon .van-tabbar {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid var(--border-color);
}

#app .van-tabbar-item__text,
#app .van-tabbar-item .van-icon {
  color: var(--text-secondary);
}

#app .van-tabbar-item--active .van-tabbar-item__text,
#app .van-tabbar-item--active .van-icon {
  color: var(--text-primary);
}

/* 中秋：花之图谱图片微弱金色光晕 */
#app.theme-moon .van-image img {
  filter: drop-shadow(0 0 6px rgba(244, 208, 63, 0.4));
}

/* ---------- 标签：日常浅绿底绿字 / 中秋透明底金描边金字 ---------- */
#app .van-tag {
  background: var(--tag-bg) !important;
  color: var(--tag-text) !important;
  border: var(--tag-border);
}

/* ---------- 按钮：主按钮主题渐变 / 次按钮透明底强调色描边 ---------- */
#app .van-button--primary {
  background: var(--btn-primary-bg) !important;
  border: none;
  color: var(--btn-primary-text) !important;
}

#app .van-button--plain {
  background: transparent !important;
  border: 1px solid var(--tag-text) !important;
  color: var(--tag-text) !important;
}

#app .van-button--default {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
}

/* ---------- 公告栏 / 通知条（跑马灯、花神公告） ---------- */
#app .van-notice-bar {
  background: var(--bg-card) !important;
  color: var(--text-primary) !important;
}

/* ---------- Tabs（商店/背包/花房/排行/帮助页签） ----------
   注意：Vant 2.13.2 没有 CSS 变量体系（编译产物里只有 var(--van-icon-font-family)），
   只能按类名覆盖。白色块来自**内层** `.van-tabs__nav{background-color:#fff}`，
   而不是外层 `.van-tabs__wrap`；这里让 nav 透明，露出已主题化的 wrap 底色。 */
#app .van-tabs__wrap {
  background: var(--bg-nav);
}

#app .van-tabs__nav {
  background: transparent;
}

#app.theme-moon .van-tabs__wrap {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

/* 深色下补一条分隔线，避免页签与内容糊在一起 */
#app.theme-moon .van-tabs__nav {
  border-bottom: 1px solid var(--border-color);
}

#app .van-tab {
  color: var(--text-secondary);
}

#app .van-tab--active {
  color: var(--tag-text);
}

#app .van-tabs__line {
  background: var(--tag-text);
}

/* ---------- 搜索框 / 输入 ---------- */
#app .van-search {
  background: transparent;
}

/* 搜索框内部的浅灰胶囊（Vant 默认 #f7f8fa）是最容易被忽略的白块来源 */
#app .van-search__content {
  background: var(--bg-card);
}

#app .van-search__action {
  color: var(--text-primary);
}

#app .van-search .van-cell,
#app .van-field {
  background: var(--bg-card);
}

#app .van-field__label {
  color: var(--text-primary);
}

#app .van-field__word-limit {
  color: var(--text-secondary);
}

#app .van-field__control {
  color: var(--text-primary);
}

#app .van-field__control::placeholder {
  color: var(--text-secondary);
}

/* ---------- 弹层 / 对话框 / 下拉 ---------- */
#app .van-popup,
#app .van-dialog {
  background: var(--bg-card) !important;
}

#app.theme-moon .van-popup,
#app.theme-moon .van-dialog {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

#app .van-dialog__header,
#app .van-dialog__message {
  color: var(--text-primary);
}

/* 对话框底部两个按钮：Vant 只给了 flex/height，底色来自按钮自身，
   深色下会露出浅色块，这里显式跟随卡片底色 */
#app .van-dialog__cancel,
#app .van-dialog__confirm {
  background: var(--bg-card);
  color: var(--tag-text);
}

/* ---------- 单选 / 复选（染色选择等） ---------- */
#app .van-radio__label,
#app .van-checkbox__label {
  color: var(--text-primary);
}

#app .van-radio__icon .van-icon,
#app .van-checkbox__icon .van-icon {
  border-color: var(--border-color);
}

#app .van-radio__icon--checked .van-icon,
#app .van-checkbox__icon--checked .van-icon {
  background-color: var(--tag-text);
  border-color: var(--tag-text);
}

#app.theme-moon .van-radio__icon--checked .van-icon,
#app.theme-moon .van-checkbox__icon--checked .van-icon {
  /* 中秋主题下勾选态用深色勾，配金色圆点对比更清楚 */
  color: #0d1b2a;
}

#app .van-picker {
  background: var(--bg-card);
}

#app .van-picker-column,
#app .van-picker__title,
#app .van-picker__cancel,
#app .van-picker__confirm {
  color: var(--text-primary);
}

/* ---------- 分页 / 步进器等小件 ---------- */
#app .van-pagination__item {
  color: var(--text-primary);
  background: var(--bg-card);
}

#app .van-pagination__item--active {
  color: var(--tag-text);
}

#app .van-pagination__item--disabled {
  color: var(--text-secondary);
  background: var(--bg-card);
}

/* 步进器（数量加减）：Vant 默认整组浅灰底 #f2f3f5 + 深色数字 #323233，
   深色主题下输入框会成为灰白块，这里统一改为卡片底 + 主文字色。
   （theme="round" 时 Vant 已把 input 设为透明，此规则对两种形态都安全） */
#app .van-stepper .van-stepper__minus,
#app .van-stepper .van-stepper__plus {
  background: var(--bg-card);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

#app .van-stepper .van-stepper__input {
  background: transparent;
  color: var(--text-primary);
}

#app .van-stepper .van-stepper__minus--disabled,
#app .van-stepper .van-stepper__plus--disabled {
  background: var(--bg-card);
  color: var(--text-secondary);
  opacity: 0.5;
}
</style>